import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
} from "@tanstack/react-table";
import { useDispatch } from "react-redux";
import {
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { Button } from "components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import { REQUEST } from "utils/reducers";
import { INQUIRY_TYPE_TO_REQUEST_TYPE, STATUS } from "utils/constants";
import { ParseTime } from "utils/parseTime";
import fetchRequest from "store/thunks/fetchRequest";
import StatusBadge from "./components/StatusBadge";
import DatePicker from "./components/DatePicker";
import StatusFilterDropdown from "./components/StatusFilterDropdown";
import ClinicFilterDropdown from "./components/ClinicFilterDropdown";
import DataTableColumnHeader from "./components/DataTableColumnHeader";

interface RequestRowInterface {
  inquiry_id: string;
  patient_name: string;
  inquiry_type: string;
  inquiry_status: string;
  inquiry_summary: string;
  inquiry_creation_ts: string;
  claims_date_of_service: string;
  inquiry_last_updated_ts?: string;
  practice_id?: string;
  practice_name?: string;
}

interface RequestListProps {
  inboxCount: number;
  requestRows: Array<RequestRowInterface>;
  reloadPage: Function;
  isTeamInbox: boolean;
  practices?: Array<{ id: string; name: string }>;
  clinicFilter: string[];
  setClinicFilter: (filter: string[]) => void;
}

const columnHelper = createColumnHelper<RequestRowInterface>();

// Custom tooltip component for buttons
function ButtonTooltip({
  children,
  content,
}: {
  children: React.ReactNode;
  content: string;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="inline-block">{children}</div>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-2 text-sm rounded-md bg-white"
        side="top"
        align="center"
      >
        {content}
      </PopoverContent>
    </Popover>
  );
}

function RequestList({
  inboxCount,
  requestRows,
  reloadPage,
  isTeamInbox,
  practices,
  clinicFilter,
  setClinicFilter,
}: RequestListProps) {
  const [isFiltersOpen, setFiltersOpen] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([
    { id: "inquiry_creation_ts", desc: true },
  ]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [pageSize, setPageSize] = useState(25);
  const [dateRangeError, setDateRangeError] = useState<string>("");

  const dispatch = useDispatch();

  // Date range validation
  const validateDateRange = (
    start: Date | undefined,
    end: Date | undefined
  ) => {
    if (start && end && start > end) {
      setDateRangeError("Start date cannot be later than end date");
      return false;
    }
    setDateRangeError("");
    return true;
  };

  // Handle start date changes with validation
  const handleStartDateChange = (date: Date | undefined) => {
    setStartDate(date);
    validateDateRange(date, endDate);
  };

  // Handle end date changes with validation
  const handleEndDateChange = (date: Date | undefined) => {
    setEndDate(date);
    validateDateRange(startDate, date);
  };

  // Helper function for status descriptions
  const getStatusDescription = useCallback((statusValue: string): string => {
    const descriptions: Record<string, string> = {
      SUCCESS: "Completed successfully",
      INVALID: "Needs to be actioned",
      UNSUCCESSFUL: "Cannot be resolved",
      IN_PROGRESS: "Currently being processed",
      PENDING_MANUAL_CALL: "Manual call required",
      PENDING: "Awaiting processing",
      POSTPONED: "Postponed",
      PARTIAL_SUCCESS: "Partial success",
      PENDING_PROCESSING: "Processing",
      PENDING_REVIEW: "Under review",
      CANCELLED: "Request cancelled",
      UNKNOWN: "Status unknown",
    };
    return descriptions[statusValue] || "No description available";
  }, []);

  // Status options for filtering - dynamically generated from STATUS constants
  const statusOptions = useMemo(
    () =>
      Object.entries(STATUS).map(([value, label]) => ({
        value,
        label,
        description: getStatusDescription(value),
      })),
    [getStatusDescription]
  );

  // Get unique status values from the data
  const availableStatuses = useMemo(() => {
    const statuses = [...new Set(requestRows.map((row) => row.inquiry_status))];
    return statusOptions.filter((option) => statuses.includes(option.value));
  }, [requestRows, statusOptions]);

  const availableClinics = useMemo(() => {
    const clinicNames = new Set<string>();
    (practices ?? []).forEach((practice) => {
      if (practice.name && practice.name.trim() !== "") {
        clinicNames.add(practice.name);
      }
    });
    requestRows.forEach((row) => {
      if (row.practice_name && row.practice_name.trim() !== "") {
        clinicNames.add(row.practice_name);
      }
    });
    return [...clinicNames].sort().map((clinic) => ({
      value: clinic,
      label: clinic,
    }));
  }, [requestRows, practices]);

  // Check if any rows have DOS data to determine if we should show the column
  const hasDosData = useMemo(() => {
    return requestRows.some(
      (row) =>
        row.claims_date_of_service && row.claims_date_of_service.trim() !== ""
    );
  }, [requestRows]);

  // Check if any rows have clinic data
  const hasClinicData = useMemo(() => {
    return requestRows.some(
      (row) => row.practice_name && row.practice_name.trim() !== ""
    );
  }, [requestRows]);

  // Memoized filtered data for performance
  const filteredData = useMemo(() => {
    let filtered = requestRows;

    // Date range filter - only apply if there's no validation error
    if ((startDate || endDate) && !dateRangeError) {
      filtered = filtered.filter((row) => {
        const rowDate = new Date(row.inquiry_creation_ts);

        if (startDate && endDate) {
          return rowDate >= startDate && rowDate <= endDate;
        } else if (startDate) {
          return rowDate >= startDate;
        } else if (endDate) {
          return rowDate <= endDate;
        }
        return true;
      });
    }

    // Status filter
    if (statusFilter.length > 0) {
      filtered = filtered.filter((row) =>
        statusFilter.includes(row.inquiry_status)
      );
    }

    if (clinicFilter.length > 0) {
      filtered = filtered.filter(
        (row) =>
          !!row.practice_name && clinicFilter.includes(row.practice_name)
      );
    }

    return filtered;
  }, [requestRows, startDate, endDate, statusFilter, clinicFilter, dateRangeError]);

  const columns = useMemo(() => {
    const baseColumns = [
      columnHelper.accessor("patient_name", {
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Patient" />
        ),
        cell: (info) => (
          <div className="font-medium text-gray-900 text-sm max-w-[150px] truncate">
            {info.getValue()}
          </div>
        ),
        size: 150,
        minSize: 100,
        maxSize: 150,
      }),
    ];

    // Conditionally add Clinic column only if there's data
    if (hasClinicData) {
      baseColumns.push(
        columnHelper.accessor("practice_name", {
          header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Clinic" />
          ),
          cell: (info) => {
            const value = info.getValue();
            return value ? (
              <div className="text-xs text-gray-700 max-w-[150px] truncate">
                {value}
              </div>
            ) : (
              <div className="text-xs text-gray-400">—</div>
            );
          },
          meta: {
            className: "hidden md:table-cell",
          },
          size: 150,
          minSize: 100,
        })
      );
    }

    // Add remaining columns
    baseColumns.push(
      columnHelper.accessor("inquiry_type", {
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Type" />
        ),
        cell: (info) => (
          <div className="text-xs text-gray-700">
            {INQUIRY_TYPE_TO_REQUEST_TYPE[info.getValue()] || info.getValue()}
          </div>
        ),
        meta: {
          className: "hidden sm:table-cell",
        },
        size: 120,
        minSize: 80,
      }),
      columnHelper.accessor("inquiry_status", {
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Status" />
        ),
        cell: (info) => <StatusBadge status={info.getValue()} />,
        size: 100,
        minSize: 80,
      })
    );

    // Conditionally add DOS column only if there's data
    if (hasDosData) {
      baseColumns.push(
        columnHelper.accessor("claims_date_of_service", {
          header: ({ column }) => (
            <DataTableColumnHeader column={column} title="DOS" />
          ),
          cell: (info) => {
            const value = info.getValue();
            return value ? (
              <div className="text-xs text-gray-600">{value}</div>
            ) : (
              <div className="text-xs text-gray-400">—</div>
            );
          },
          meta: {
            className: "hidden lg:table-cell",
          },
          size: 80,
          minSize: 60,
        })
      );
    }

    // Add remaining columns
    baseColumns.push(
      columnHelper.accessor("inquiry_summary", {
        header: "Summary",
        cell: (info) => (
          <div className="text-xs text-gray-700 max-w-xs truncate leading-tight">
            {info.getValue()}
          </div>
        ),
        enableSorting: false,
        meta: {
          className: "hidden md:table-cell",
        },
        size: 300,
        minSize: 200,
      }),
      columnHelper.accessor("inquiry_last_updated_ts", {
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Updated" />
        ),
        cell: (info) => {
          const [month, , time] = ParseTime(info.getValue());
          return (
            <div className="text-xs text-gray-600">
              <div>{month}</div>
              <div className="text-xs text-gray-500 leading-none">{time}</div>
            </div>
          );
        },
        sortingFn: (rowA, rowB, columnId) => {
          const a = new Date(rowA.getValue(columnId)).getTime();
          const b = new Date(rowB.getValue(columnId)).getTime();
          return a < b ? -1 : a > b ? 1 : 0;
        },
        size: 90,
        minSize: 70,
      }),
      columnHelper.accessor("inquiry_creation_ts", {
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Created" />
        ),
        cell: (info) => {
          const [month, , time] = ParseTime(info.getValue());
          return (
            <div className="text-xs text-gray-600">
              <div>{month}</div>
              <div className="text-xs text-gray-500 leading-none">{time}</div>
            </div>
          );
        },
        sortingFn: (rowA, rowB, columnId) => {
          const a = new Date(rowA.getValue(columnId)).getTime();
          const b = new Date(rowB.getValue(columnId)).getTime();
          return a < b ? -1 : a > b ? 1 : 0;
        },
        size: 90,
        minSize: 70,
      })
    );

    return baseColumns;
  }, [hasDosData, hasClinicData]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      pagination: {
        pageSize: pageSize,
      },
    },
  });

  const handleRowClick = (inquiryId: string) => {
    dispatch({ type: REQUEST.display });
    dispatch(fetchRequest(inquiryId) as any);
  };

  // Update table page size when pageSize state changes
  useEffect(() => {
    table.setPageSize(pageSize);
  }, [pageSize, table]);

  return (
    <div className="w-full h-full flex flex-col bg-background">
      {/* Header Section */}
      <div className="flex flex-col space-y-4 p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold tracking-tight">Requests</h2>
            <span className="text-sm text-muted-foreground">
              {filteredData.length} of {inboxCount} total
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFiltersOpen(!isFiltersOpen)}
              className="lg:hidden h-8 px-3"
            >
              <ChevronDown
                className={`h-4 w-4 mr-1 transition-transform ${
                  isFiltersOpen ? "rotate-180" : ""
                }`}
              />
              Filters
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => reloadPage(true, isTeamInbox)}
              className="h-8 w-8 p-0"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Filters Section */}
        <div
          className={`${
            isFiltersOpen ? "block" : "hidden"
          } lg:flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mt-4 lg:mt-0`}
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:flex-wrap">
            {/* Status Filter */}
            <StatusFilterDropdown
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              availableStatuses={availableStatuses}
            />

            {availableClinics.length > 0 && (
              <ClinicFilterDropdown
                clinicFilter={clinicFilter}
                setClinicFilter={setClinicFilter}
                availableClinics={availableClinics}
              />
            )}

            {/* Date Range Filters */}
            <div className="flex flex-col space-y-2">
              <div className="flex items-center space-x-2 flex-wrap">
                <span className="text-sm text-muted-foreground mb-2 sm:mb-0">
                  Created between:
                </span>
                <DatePicker
                  date={startDate}
                  setDate={handleStartDateChange}
                  placeholder="From date"
                  maxDate={endDate}
                  error={!!dateRangeError}
                />
                <DatePicker
                  date={endDate}
                  setDate={handleEndDateChange}
                  placeholder="To date"
                  minDate={startDate}
                  error={!!dateRangeError}
                />
                {(startDate || endDate) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setStartDate(undefined);
                      setEndDate(undefined);
                      setDateRangeError("");
                    }}
                    className="h-8 px-2 text-xs"
                  >
                    Clear
                  </Button>
                )}
              </div>
              {dateRangeError && (
                <p className="text-xs text-red-600 font-medium">
                  {dateRangeError}
                </p>
              )}
            </div>
          </div>

          {/* Page Size Control */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">Rows per page</span>
            <Select
              value={pageSize.toString()}
              onValueChange={(value) => setPageSize(Number(value))}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent side="top">
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Data Table Container */}
      <div className="flex-1 flex flex-col overflow-visible">
        {/* Desktop Table View */}
        <div className="hidden lg:flex flex-1 flex-col min-h-0">
          <div className="flex-1 overflow-auto relative">
            <Table>
              <TableHeader className="sticky top-0 bg-white z-0">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className={header.column.columnDef.meta?.className}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className="cursor-pointer hover:bg-gray-50 transition-colors duration-150"
                      onClick={() => handleRowClick(row.original.inquiry_id)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          className={cell.column.columnDef.meta?.className}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden flex-1 overflow-auto p-2 space-y-3">
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => {
              const request = row.original;
              const [createdMonth] = ParseTime(request.inquiry_creation_ts);
              const lastUpdated = request.inquiry_last_updated_ts
                ? ParseTime(request.inquiry_last_updated_ts)[0]
                : null;
              return (
                <div
                  key={row.id}
                  className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 cursor-pointer hover:shadow-md hover:border-gray-300 transition-all duration-200"
                  onClick={() => handleRowClick(request.inquiry_id)}
                >
                  <div className="flex justify-between items-start">
                    <span className="font-semibold text-gray-800">
                      {request.patient_name}
                    </span>
                    <StatusBadge status={request.inquiry_status} />
                  </div>
                  {hasClinicData && request.practice_name && (
                    <p className="text-xs text-gray-600 mt-1 font-medium">
                      Clinic: {request.practice_name}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 mt-2 truncate">
                    {request.inquiry_summary}
                  </p>
                  <div className="flex justify-between items-center mt-3 text-xs text-gray-500 flex-wrap gap-1">
                    <span>
                      {INQUIRY_TYPE_TO_REQUEST_TYPE[request.inquiry_type] ||
                        request.inquiry_type}
                    </span>
                    {hasDosData && (
                      <span>DOS: {request.claims_date_of_service || "—"}</span>
                    )}
                    <span>Created: {createdMonth}</span>
                    {lastUpdated && <span>Updated: {lastUpdated}</span>}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-24 text-gray-500">No results.</div>
          )}
        </div>
      </div>

      {/* Pagination - Sticks to bottom of table */}
      <div className="relative sticky bottom-0  flex items-center justify-between px-4 py-4 border-t border-border bg-white">
        <div className="text-sm text-muted-foreground">
          {filteredData.length === 0 ? (
            "There are 0 results."
          ) : (
            <>
              Showing{" "}
              {table.getState().pagination.pageIndex *
                table.getState().pagination.pageSize +
                1}{" "}
              to{" "}
              {Math.min(
                (table.getState().pagination.pageIndex + 1) *
                  table.getState().pagination.pageSize,
                filteredData.length
              )}{" "}
              of {filteredData.length} row(s).
            </>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <ButtonTooltip
            content={
              !table.getCanPreviousPage()
                ? "Already on first page"
                : "Go to first page"
            }
          >
            <Button
              variant="outline"
              className={`h-8 w-8 p-0 ${
                !table.getCanPreviousPage()
                  ? "opacity-30 cursor-not-allowed bg-gray-50 text-gray-400 border-gray-200"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">Go to first page</span>
              <ChevronLeft className="h-4 w-4" />
              <ChevronLeft className="h-4 w-4 -ml-2" />
            </Button>
          </ButtonTooltip>
          <ButtonTooltip
            content={
              !table.getCanPreviousPage()
                ? "Already on first page"
                : "Go to previous page"
            }
          >
            <Button
              variant="outline"
              className={`h-8 w-8 p-0 ${
                !table.getCanPreviousPage()
                  ? "opacity-30 cursor-not-allowed bg-gray-50 text-gray-400 border-gray-200"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">Go to previous page</span>
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </ButtonTooltip>
          <div className="hidden sm:flex w-[100px] items-center justify-center text-sm font-medium">
            Page {table.getState().pagination.pageIndex + 1}
          </div>
          <ButtonTooltip
            content={
              !table.getCanNextPage()
                ? "Already on last page"
                : "Go to next page"
            }
          >
            <Button
              variant="outline"
              className={`h-8 w-8 p-0 ${
                !table.getCanNextPage()
                  ? "opacity-30 cursor-not-allowed bg-gray-50 text-gray-400 border-gray-200"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">Go to next page</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </ButtonTooltip>
        </div>
      </div>
    </div>
  );
}

export default RequestList;
